import {Component, computed, inject, resource, signal, viewChild} from '@angular/core';
import {take} from 'rxjs/operators';
import {confirm} from 'devextreme/ui/dialog';
import {FacturationClientService} from '../../../../core/services/entities/facturation-client.service';
import {
  ParametrageFacturationHierarchy
} from '../../../../core/models/facturation/client/parametrage-facturation-hierarchy';
import {NamedItem} from '../../../../core/models/named-item';
import {
  PlcProduitComplementaireFacturableDTO
} from '../../../../core/dtos/produit/declinaison/complementaire/plc-produit-complementaire-facturable-dto';
import {
  PeriodeConditionnementNonParametreeDTO
} from '../../../../core/dtos/produit/declinaison/complementaire/periode-conditionnement-non-parametree-dto';
import {
  LigneParametrageFacturationProduitComplementaireDTO
} from '../../../../core/dtos/produit/declinaison/complementaire/ligne-parametrage-facturation-produit-complementaire-dto';
import {FacturationProduitComplementaireService} from '../../../../core/services/entities/facturation-produit-complementaire.service';
import {
  LigneParametrageFacturationProduitComplementaire
} from '../../../../core/models/facturation/client/ligne-parametrage-facturation-produit-complementaire';
import {applyTauxOrPrixToPeriodeList} from '../../../../core/models/facturation/client/periode-parametrage-facturation';
import {
  PeriodeParametrageFacturationProduitComplementaire
} from '../../../../core/models/facturation/client/periode-parametrage-facturation-produit-complementaire';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {TaxesService} from '../../../../core/services/entities/taxes.service';
import {ToastService} from '../../../../core/services/technique/toast.service';
import {MSG_KEY, MSG_SEVERITY} from '../../../../core/constants';
import {
  LigneParametrageProduitComplementaireDataGridComponent,
  PeriodeBadgeClickEvent
} from '../ligne-parametrage-produit-complementaire-data-grid/ligne-parametrage-produit-complementaire-data-grid.component';

/** Grille visée par une action de masse (modification, période de prix, suppression) : liste principale ou staging. */
type GridTarget = 'main' | 'staging';

@Component({
  selector: 'yo-facturation-produit-complementaire-parametrage',
  templateUrl: './facturation-produit-complementaire-parametrage.component.html',
  styleUrls: ['./facturation-produit-complementaire-parametrage.component.scss'],
  standalone: false
})
export class FacturationProduitComplementaireParametrageComponent {

  readonly ButtonThemeEnum = ButtonThemeEnum;

  private readonly facturationClientSvc = inject(FacturationClientService);
  private readonly produitComplementaireSvc = inject(FacturationProduitComplementaireService);
  private readonly taxesSvc = inject(TaxesService);
  private readonly toastSvc = inject(ToastService);

  readonly isAddAreaCollapsed = signal(true);

  readonly selectedPointFacturationIdList = signal<number[]>([]);
  readonly selectedPlcIdList = signal<number[]>([]);
  readonly selectedProduitComplementaireIdList = signal<number[]>([]);

  readonly taxeList = this.taxesSvc.taxeList.value;

  readonly isStagingPopupVisible = signal(false);
  readonly stagingList = signal<LigneParametrageFacturationProduitComplementaire[]>([]);

  /** Deux instances du même composant de grille coexistent (liste principale + popup de staging) : ciblées par référence de template, pas par type. */
  private readonly gridMain = viewChild<LigneParametrageProduitComplementaireDataGridComponent>('gridMain');
  private readonly gridPopup = viewChild<LigneParametrageProduitComplementaireDataGridComponent>('gridPopup');

  readonly isModificationPopupDisplayed = signal(false);
  readonly selectedTaxeIdForModification = signal<number | null>(null);

  readonly isPeriodePrixPopupDisplayed = signal(false);
  readonly selectedPrixForPeriode = signal<number | null>(null);
  readonly selectedTauxForPeriode = signal<number | null>(null);
  readonly selectedDateForPeriode = signal<Date>(new Date());

  /** Grille visée par les popups de modification de masse / suppression : renseignée à l'ouverture du popup ou au clic sur Supprimer. */
  private modificationTarget: GridTarget = 'staging';

  private readonly hierarchyResource = resource({
    defaultValue: null as ParametrageFacturationHierarchy | null,
    loader: async () => {
      const response = await this.facturationClientSvc.findLigneParametrageHierarchy().pipe(take(1)).toPromise();
      return response?.one ?? null;
    }
  });
  readonly pointFacturationList = computed(() => this.hierarchyResource.value()?.pointsFacturation ?? []);

  readonly plcList = computed(() => {
    const hierarchy = this.hierarchyResource.value();
    return hierarchy ? this.dedupeByPointFacturation(this.selectedPointFacturationIdList(), hierarchy.pointsLivraisonByPointFacturationId) : [];
  });

  /** PLC -> point de facturation, dérivé de la hiérarchie : un PLC n'appartient qu'à un seul point de facturation. */
  private readonly pointFacturationIdByPlcId = computed(() => {
    const hierarchy = this.hierarchyResource.value();
    const map = new Map<number, number>();
    if (!hierarchy) {
      return map;
    }
    Object.keys(hierarchy.pointsLivraisonByPointFacturationId).forEach(pointFacturationId => {
      (hierarchy.pointsLivraisonByPointFacturationId[pointFacturationId] ?? [])
        .forEach(plc => map.set(plc.id, Number(pointFacturationId)));
    });
    return map;
  });

  /** Une ligne par (PLC, produit) facturable, nécessaire pour ne générer que des paires valides à l'ajout. */
  private readonly produitComplementairePairListResource = resource({
    params: this.selectedPlcIdList,
    defaultValue: [] as PlcProduitComplementaireFacturableDTO[],
    loader: ({params}) => this.produitComplementaireSvc.findFacturablesByPlcIdList(params)
  });
  private readonly produitComplementairePairList = this.produitComplementairePairListResource.value;

  /** Périodes de conditionnement (commande) pas encore couvertes par une période de facturation au même conditionnement. */
  private readonly periodesNonParametreesResource = resource({
    params: this.selectedPlcIdList,
    defaultValue: [] as PeriodeConditionnementNonParametreeDTO[],
    loader: ({params}) => this.produitComplementaireSvc.findPeriodesNonParametreesByPlcIdList(params)
  });
  private readonly periodesNonParametreesList = this.periodesNonParametreesResource.value;

  /** Vue dédupliquée par produit pour le multi-select : la sélection porte sur le produit, pas sur une paire. */
  readonly produitComplementaireList = computed(() => {
    const produitByIdMap = new Map<number, PlcProduitComplementaireFacturableDTO>();
    this.produitComplementairePairList().forEach(pair => produitByIdMap.set(pair.produitDeclinaisonId, pair));
    return [...produitByIdMap.values()];
  });

  /** Libellés indexés par identifiant : une seule recherche par référentiel, quel que soit le nombre de lignes. */
  private readonly plcLibelleByIdMap = computed(() =>
    new Map<number, string>(this.plcList().map(plc => [plc.id, plc.libelle] as [number, string])));

  private readonly taxeLibelleByIdMap = computed(() =>
    new Map<number, string>((this.taxeList() ?? []).map(taxe => [taxe.id, taxe.libelle] as [number, string])));

  /** Toutes les lignes déjà paramétrées (aucun filtre PLC) — mise à jour après un enregistrement réussi. */
  private readonly ligneParametrageResource = resource({
    defaultValue: [] as LigneParametrageFacturationProduitComplementaireDTO[],
    loader: () => this.produitComplementaireSvc.findLignesByPlcIdList([])
  });

  /**
   * Modifications en attente sur la grille principale (taxe / période de prix), clé par paire (plcId, produitId) —
   * à l'instar de gridChanges dans yo-facturation-client-famille-facturation-parametrage : Modifier/Modifier les
   * périodes de prix n'enregistrent plus tout de suite, seul le bouton Enregistrer les persiste. Supprimer reste
   * immédiat (rien à revenir en arrière dessus une fois confirmé).
   */
  private readonly mainChangesMap = signal(new Map<string, LigneParametrageFacturationProduitComplementaire>());

  readonly hasMainChanges = computed(() => this.mainChangesMap().size > 0);

  readonly ligneParametrageList = computed(() => {
    const changesMap = this.mainChangesMap();
    return this.ligneParametrageResource.value().map(dto => {
      const ligne = this.toLigneParametrageFacturationProduitComplementaire(dto);
      return changesMap.get(this.buildPairKey(ligne.plcId, ligne.produitDeclinaisonId)) ?? ligne;
    });
  });

  /**
   * Périodes de facturation dont le conditionnement n'est plus porté par aucune période de conditionnement
   * commande, ou dont le couple (PLC, produit) n'est plus facturable (signal serveur `conditionnementObsolete`,
   * malgré son nom, voir LigneParametrageFacturationProduitComplementaireJpaRepo ; distinct de `periodeToDelete` qui ne concerne que le
   * remplacement d'une période par une autre plus récente sur la même ligne) — nettoyables en un clic, après
   * confirmation, par `cleanObsoletePeriodes`.
   */
  private readonly obsoletePeriodeIdList = computed(() =>
    this.ligneParametrageList()
        .reduce((periodeList, ligne) => periodeList.concat(ligne.periodeParametrageFacturationList), [] as PeriodeParametrageFacturationProduitComplementaire[])
        .filter(periode => periode.conditionnementObsolete && periode.id != null)
        .map(periode => periode.id));

  readonly hasObsoletePeriodes = computed(() => this.obsoletePeriodeIdList().length > 0);

  /**
   * Ne supprime que les périodes signalées obsolètes — jamais les lignes qui les portent directement, mais le
   * backend supprime lui-même toute ligne qui se retrouverait sans aucune période (pas de ligne de paramétrage
   * vide en base) : la confirmation prévient l'utilisateur de ce cas plutôt que de le découvrir après coup.
   * Contrairement à `deleteSelected`, l'utilisateur n'a rien sélectionné — la confirmation liste le nombre de
   * périodes concernées et lui laisse la main pour vérifier `plc__produit_declinaison_complementaire__periode`
   * avant de valider.
   */
  cleanObsoletePeriodes = async (): Promise<void> => {
    const periodeIdList = this.obsoletePeriodeIdList();
    const periodeIdSet = new Set(periodeIdList);
    // Calculé sur ligneParametrageList() (persistant + éditions mainChangesMap en attente) : une édition en attente
    // ne peut qu'ajouter une période locale sans id, jamais retirer une période obsolète déjà persistée — ce compte
    // reste donc fidèle à ce que le backend fera réellement. À revérifier si applyToSelected/mainChangesMap évolue.
    const ligneASupprimerCount = this.ligneParametrageList()
      .filter(ligne => ligne.periodeParametrageFacturationList.length > 0
        && ligne.periodeParametrageFacturationList.every(periode => periodeIdSet.has(periode.id)))
      .length;
    const ligneWarning = ligneASupprimerCount > 0
      ? ` ${ligneASupprimerCount} ligne(s) sans période seront aussi supprimées.`
      : '';

    const isDeletionValidated = await confirm(
      `Supprimer ${periodeIdList.length} période(s) obsolète(s) ?${ligneWarning}`,
      'Nettoyage des périodes obsolètes');
    if (!isDeletionValidated) {
      return;
    }

    const success = await this.persistLignes([], [], [], periodeIdList);
    if (!success) {
      return;
    }
    this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Périodes obsolètes supprimées avec succès`);
  };

  /**
   * `produitComplementairePairListResource` et `periodesNonParametreesResource` ne se rechargent automatiquement
   * que si `selectedPlcIdList` change — si l'utilisateur modifie les périodes de conditionnement (commande) dans un
   * autre écran puis revient sans changer sa sélection de PLC, `addLignesParametrage` calculerait sur des données
   * obsolètes. Forcer un reload à chaque ouverture du panneau d'ajout garantit des candidats à jour.
   */
  openAddArea = (): void => {
    this.produitComplementairePairListResource.reload();
    this.periodesNonParametreesResource.reload();
    this.isAddAreaCollapsed.set(false);
  };

  closeAddArea = (): void => this.isAddAreaCollapsed.set(true);

  /** Changer de point de facturation change la liste des PLC proposés : les sélections suivantes n'ont plus de sens. */
  onPointFacturationIdListChange = (idList: number[]): void => {
    this.selectedPointFacturationIdList.set(idList ?? []);
    this.selectedPlcIdList.set([]);
    this.selectedProduitComplementaireIdList.set([]);
  };

  /** Changer de PLC change la liste des produits proposés : la sélection précédente n'a plus de sens. */
  onPlcIdListChange = (idList: number[]): void => {
    this.selectedPlcIdList.set(idList ?? []);
    this.selectedProduitComplementaireIdList.set([]);
  };

  onProduitComplementaireIdListChange = (idList: number[]): void =>
    this.selectedProduitComplementaireIdList.set(idList ?? []);

  readonly isReadyToAdd = computed(() =>
    this.selectedPlcIdList().length > 0 && this.selectedProduitComplementaireIdList().length > 0);

  /**
   * Construit les lignes à créer/modifier à partir de la sélection courante, en les rapprochant des lignes déjà
   * persistées pour ces PLC (mêmes id/taxe/période, prêtes à être modifiées plutôt que dupliquées), puis ouvre
   * la grille de staging (même composant que la grille principale) pour renseigner taxe et prix.
   *
   * Le rapprochement lit ligneParametrageList() (déjà chargée en totalité, incluant les modifications de la
   * grille principale pas encore enregistrées) plutôt que de refaire un appel réseau : sans ça, une modification
   * en attente sur mainChangesMap serait ignorée ici et écrasée par sa version d'avant modification au save.
   */
  addLignesParametrage = (): void => {
    const selectedProduitIdList = this.selectedProduitComplementaireIdList();
    const validPairList = this.produitComplementairePairList()
                               .filter(pair => selectedProduitIdList.includes(pair.produitDeclinaisonId));

    const selectedPlcIdList = this.selectedPlcIdList();
    const existingByKeyMap = new Map<string, LigneParametrageFacturationProduitComplementaire>();
    this.ligneParametrageList()
      .filter(ligne => selectedPlcIdList.includes(ligne.plcId))
      .forEach(ligne => existingByKeyMap.set(this.buildPairKey(ligne.plcId, ligne.produitDeclinaisonId), ligne));

    this.stagingList.set(validPairList.map(pair => this.buildLigneStaging(pair, existingByKeyMap)));
    this.isStagingPopupVisible.set(true);
  };

  /**
   * Périodes de conditionnement du pair (plcId, produitDeclinaisonId) sans période de facturation au même
   * conditionnement — importées vides (aucun prix) : le popup "Appliquer un prix/taux" les remplit ensuite en les
   * retrouvant par dateDebut, sans mécanisme dédié supplémentaire.
   */
  private buildPeriodesAImporter = (pair: PlcProduitComplementaireFacturableDTO): PeriodeParametrageFacturationProduitComplementaire[] =>
    this.periodesNonParametreesList()
        .filter(periode => periode.plcId === pair.plcId && periode.produitDeclinaisonId === pair.produitDeclinaisonId)
        .map(periode => ({
          id: null,
          dateDebut: periode.dateDebut,
          uniteDeMesureId: periode.uniteDeMesureId,
          mcpConditionnementVarianteId: periode.mcpConditionnementVarianteId,
          uniteDeMesure: periode.uniteDeMesure,
          prixVenteHT: null,
          periodeToDelete: false,
          conditionnementObsolete: false
        }));

  private buildLigneStaging = (pair: PlcProduitComplementaireFacturableDTO,
                                existingByKeyMap: Map<string, LigneParametrageFacturationProduitComplementaire>): LigneParametrageFacturationProduitComplementaire => {
    const periodesAImporter = this.buildPeriodesAImporter(pair);

    const existing = existingByKeyMap.get(this.buildPairKey(pair.plcId, pair.produitDeclinaisonId));
    if (existing) {
      return {...existing, periodeParametrageFacturationList: [...existing.periodeParametrageFacturationList, ...periodesAImporter]};
    }
    const pointFacturationId = this.pointFacturationIdByPlcId().get(pair.plcId);
    if (pointFacturationId == null) {
      throw new Error(`Aucun point de facturation trouvé pour le point de livraison ${pair.plcId}`);
    }
    return {
      id: null,
      pointFacturationId,
      plcId: pair.plcId,
      plcLibelle: this.plcLibelleByIdMap().get(pair.plcId) ?? '',
      produitDeclinaisonId: pair.produitDeclinaisonId,
      produitLibelle: pair.libelle,
      taxeId: null,
      taxeLibelle: '',
      periodeParametrageFacturationList: periodesAImporter
    };
  };

  private buildPairKey = (plcId: number, produitDeclinaisonId: number): string => `${plcId}-${produitDeclinaisonId}`;

  isMainRowSelected = (): boolean => (this.gridMain()?.getSelectedData()?.length ?? 0) > 0;

  isPopupRowSelected = (): boolean => (this.gridPopup()?.getSelectedData()?.length ?? 0) > 0;

  private getGrid = (target: GridTarget): LigneParametrageProduitComplementaireDataGridComponent | undefined =>
    target === 'main' ? this.gridMain() : this.gridPopup();

  /** Prédicat d'appartenance à la sélection de la grille visée, capturée au moment de l'appel. */
  private buildIsSelected = (target: GridTarget): (ligne: LigneParametrageFacturationProduitComplementaire) => boolean => {
    const selectedKeySet = new Set((this.getGrid(target)?.getSelectedData() ?? [])
      .map(key => this.buildPairKey(key.plcId, key.produitDeclinaisonId)));
    return ligne => selectedKeySet.has(this.buildPairKey(ligne.plcId, ligne.produitDeclinaisonId));
  };

  openModificationPopup = (target: GridTarget): void => {
    this.modificationTarget = target;
    this.selectedTaxeIdForModification.set(null);
    this.isModificationPopupDisplayed.set(true);
  };

  /**
   * Clic sur la case Taxe d'une ligne : même popup que le bouton "Modifier la taxe", restreinte à cette ligne
   * (comme onPeriodeBadgeClick) et pré-remplie avec sa taxe actuelle.
   */
  onTaxeCellClick = (target: GridTarget, ligne: LigneParametrageFacturationProduitComplementaire): void => {
    this.getGrid(target)?.selectOnly({plcId: ligne.plcId, produitDeclinaisonId: ligne.produitDeclinaisonId});
    this.openModificationPopup(target);
    this.selectedTaxeIdForModification.set(ligne.taxeId);
  };

  closeModificationPopup = (): void => this.isModificationPopupDisplayed.set(false);

  applyModification = (): void => {
    const taxeId = this.selectedTaxeIdForModification();
    if (taxeId == null) {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, `Veuillez sélectionner une taxe`);
      return;
    }
    const taxeLibelle = this.taxeLibelleByIdMap().get(taxeId) ?? '';
    this.applyToSelected(this.modificationTarget, ligne => ({...ligne, taxeId, taxeLibelle}));
    this.closeModificationPopup();
  };

  openPeriodePrixPopup = (target: GridTarget, dateDebut: Date = new Date()): void => {
    this.modificationTarget = target;
    this.selectedPrixForPeriode.set(null);
    this.selectedTauxForPeriode.set(null);
    this.selectedDateForPeriode.set(dateDebut);
    this.isPeriodePrixPopupDisplayed.set(true);
  };

  /**
   * Clic sur le badge d'une période, dans la grille principale ou de staging : ouvre le popup "Période de prix"
   * avec la date de cette période déjà positionnée, et restreint la sélection à cette seule ligne — sans ça,
   * Valider s'appliquerait à toute sélection préexistante ailleurs dans la grille, pas à la ligne du badge cliqué.
   */
  onPeriodeBadgeClick = (target: GridTarget, event: PeriodeBadgeClickEvent): void => {
    this.getGrid(target)?.selectOnly({plcId: event.ligne.plcId, produitDeclinaisonId: event.ligne.produitDeclinaisonId});
    this.openPeriodePrixPopup(target, event.periode.dateDebut);
  };

  closePeriodePrixPopup = (): void => this.isPeriodePrixPopupDisplayed.set(false);

  /**
   * Prix saisi directement, ou taux de revalorisation appliqué à chaque ligne sélectionnée — logique commune avec
   * yo-facturation-client-famille-facturation-parametrage, voir applyTauxOrPrixToPeriodeList. Une ligne sans prix
   * de référence ne peut pas recevoir de taux, d'où le contrôle préalable avant d'appliquer quoi que ce soit.
   */
  applyPeriodePrix = (): void => {
    const prixVenteHT = this.selectedPrixForPeriode();
    const taux = this.selectedTauxForPeriode();
    if (prixVenteHT == null && taux == null) {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, `Veuillez saisir un prix de vente ou un taux de revalorisation`);
      return;
    }

    const dateDebut = this.selectedDateForPeriode();
    const isSelected = this.buildIsSelected(this.modificationTarget);
    const sourceList = this.modificationTarget === 'staging' ? this.stagingList() : this.ligneParametrageList();
    const hasMissingReference = sourceList
      .filter(isSelected)
      .some(ligne => applyTauxOrPrixToPeriodeList(ligne.periodeParametrageFacturationList, dateDebut, prixVenteHT, taux) == null);
    if (hasMissingReference) {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, `Aucun prix de vente défini pour appliquer un taux de revalorisation`);
      return;
    }

    this.applyToSelected(this.modificationTarget, ligne => ({
      ...ligne,
      periodeParametrageFacturationList: applyTauxOrPrixToPeriodeList(ligne.periodeParametrageFacturationList, dateDebut, prixVenteHT, taux)
    }));
    this.closePeriodePrixPopup();
  };

  /**
   * Applique la transformation aux lignes sélectionnées : mise à jour locale dans les deux cas — le staging
   * n'est jamais persisté avant l'Enregistrer de la popup, la grille principale passe par mainChangesMap et
   * n'est persistée qu'au clic sur son propre bouton Enregistrer (saveMainChanges).
   */
  private applyToSelected = (target: GridTarget,
                              updateFn: (ligne: LigneParametrageFacturationProduitComplementaire) => LigneParametrageFacturationProduitComplementaire): void => {
    const isSelected = this.buildIsSelected(target);

    if (target === 'staging') {
      this.stagingList.update(list => list.map(ligne => isSelected(ligne) ? updateFn(ligne) : ligne));
      return;
    }

    const updatedLigneList = this.ligneParametrageList().filter(isSelected).map(updateFn);
    this.mainChangesMap.update(changesMap => {
      const updated = new Map(changesMap);
      updatedLigneList.forEach(ligne => updated.set(this.buildPairKey(ligne.plcId, ligne.produitDeclinaisonId), ligne));
      return updated;
    });
  };

  /**
   * Persiste en une fois les modifications de taxe / période de prix en attente sur la grille principale.
   * persistLignes() purge déjà mainChangesMap des clés qu'il vient d'enregistrer — pas de reset ici : entre le
   * début de l'appel et sa résolution, une nouvelle modification en attente a pu être ajoutée par ailleurs et ne
   * doit pas être perdue par un vidage complet de la map.
   */
  saveMainChanges = async (): Promise<void> => {
    const lignesToEdit = [...this.mainChangesMap().values()].map(ligne => this.buildLigneDTO(ligne));
    if (lignesToEdit.length === 0) {
      return;
    }
    const success = await this.persistLignes([], lignesToEdit);
    if (!success) {
      return;
    }
    this.getGrid('main')?.clearSelection();
    this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Lignes de paramétrage mises à jour avec succès`);
  };

  /**
   * Supprime les lignes sélectionnées. En staging, une ligne pas encore persistée (id null) est simplement retirée
   * du lot ; une ligne déjà persistée (rapprochée automatiquement à l'ajout) est aussi supprimée en base — si cette
   * suppression échoue, la ligne est restaurée côté staging plutôt que de disparaître silencieusement de la vue.
   */
  deleteSelected = async (target: GridTarget): Promise<void> => {
    const isDeletionValidated = await confirm(
      'Voulez-vous vraiment supprimer les lignes de paramétrage sélectionnées ?',
      'Suppression');
    if (!isDeletionValidated) {
      return;
    }

    const isSelected = this.buildIsSelected(target);

    if (target === 'staging') {
      const removedLigneList = this.stagingList().filter(isSelected);
      const idToDeleteList = removedLigneList.filter(ligne => ligne.id != null).map(ligne => ligne.id);
      this.stagingList.update(list => list.filter(ligne => !isSelected(ligne)));
      if (idToDeleteList.length > 0) {
        const success = await this.persistLignes([], [], idToDeleteList);
        if (!success) {
          this.stagingList.update(list => [...list, ...removedLigneList]);
          return;
        }
      }
    } else {
      const idToDeleteList = this.ligneParametrageList().filter(isSelected).map(ligne => ligne.id);
      const success = await this.persistLignes([], [], idToDeleteList);
      if (!success) {
        return;
      }
    }

    this.getGrid(target)?.clearSelection();
    this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Lignes de paramétrage supprimées avec succès`);
  };

  /**
   * Enregistre les changements en base puis fusionne le résultat dans la liste principale, sans rappel réseau.
   * saveLignes() jette en cas d'échec (HttpService transforme sinon un échec en réponse vide, indiscernable
   * d'un enregistrement réussi de zéro ligne) : on l'attrape ici pour que l'appelant sache s'il doit continuer
   * (fermer sa popup, notifier un succès) ou s'arrêter (garder son état local, ne pas prétendre avoir réussi).
   */
  private persistLignes = async (lignesToAdd: LigneParametrageFacturationProduitComplementaireDTO[],
                                  lignesToEdit: LigneParametrageFacturationProduitComplementaireDTO[],
                                  ligneIdsToDelete: number[] = [],
                                  periodeIdsToDelete: number[] = []): Promise<boolean> => {
    let savedList: LigneParametrageFacturationProduitComplementaireDTO[];
    try {
      savedList = await this.produitComplementaireSvc.saveLignes({lignesToAdd, lignesToEdit, ligneIdsToDelete, periodeIdsToDelete});
    } catch {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, `Impossible d'enregistrer les lignes de paramétrage`);
      return false;
    }

    const periodeIdToDeleteSet = new Set(periodeIdsToDelete);
    /** Une ligne dont on vient de retirer la dernière période n'a plus de raison d'exister : le backend la supprime
     * lui-même (voir LigneParametrageFacturationProduitComplementaireJpaRepo.deletePeriodes côté Java) — on reproduit
     * la même règle localement plutôt que d'afficher une ligne fantôme sans période jusqu'au prochain rechargement. */
    const removePeriodesAndDropIfEmpty = <T extends {periodeParametrageFacturationList: {id?: number | null}[]}>(ligne: T): T => ({
      ...ligne,
      periodeParametrageFacturationList: ligne.periodeParametrageFacturationList.filter(periode => !periodeIdToDeleteSet.has(periode.id))
    } as T);

    // le backend ne renvoie jamais les lignes supprimées, ni les lignes dont seule une période a été retirée, dans son résultat
    this.ligneParametrageResource.update(currentList => {
      const savedByKeyMap = new Map(savedList.map(saved => [this.buildPairKey(saved.plcId, saved.produitDeclinaisonId), saved]));
      const untouchedList = currentList
        .filter(ligne => !savedByKeyMap.has(this.buildPairKey(ligne.plcId, ligne.produitDeclinaisonId)) && !ligneIdsToDelete.includes(ligne.id))
        .map(ligne => periodeIdToDeleteSet.size === 0 ? ligne : removePeriodesAndDropIfEmpty(ligne))
        .filter(ligne => ligne.periodeParametrageFacturationList.length > 0);
      return [...untouchedList, ...savedList];
    });

    /**
     * Toute ligne venant d'être enregistrée ou supprimée — par ce flux ou un autre (ex: le staging modifie une
     * ligne aussi présente dans mainChangesMap) — invalide l'éventuel instantané en attente sur la grille
     * principale : le garder ferait écraser silencieusement cet enregistrement plus récent au prochain Enregistrer.
     * Une période supprimée sur une ligne par ailleurs en attente de modification est retirée de l'instantané
     * plutôt que d'invalider toute la modification en attente — sauf si elle vide la ligne, auquel cas la
     * modification en attente n'a plus de sens (la ligne est supprimée côté serveur).
     */
    const touchedKeySet = new Set([...lignesToAdd, ...lignesToEdit].map(ligne => this.buildPairKey(ligne.plcId, ligne.produitDeclinaisonId)));
    if (touchedKeySet.size > 0 || ligneIdsToDelete.length > 0 || periodeIdToDeleteSet.size > 0) {
      this.mainChangesMap.update(changesMap => {
        const updated = new Map(changesMap);
        for (const [key, ligne] of updated) {
          if (touchedKeySet.has(key) || (ligne.id != null && ligneIdsToDelete.includes(ligne.id))) {
            updated.delete(key);
          } else if (periodeIdToDeleteSet.size > 0) {
            const remainingLigne = removePeriodesAndDropIfEmpty(ligne);
            if (remainingLigne.periodeParametrageFacturationList.length === 0) {
              updated.delete(key);
            } else {
              updated.set(key, remainingLigne);
            }
          }
        }
        return updated;
      });
    }

    return true;
  };

  /** Ce qui empêche d'enregistrer le staging, une entrée par type de manque, vide si tout est renseigné. */
  private readonly stagingErrorList = computed(() => {
    const stagingList = this.stagingList();
    const ligneSansTaxeCount = stagingList.filter(ligne => ligne.taxeId == null).length;
    const ligneSansPeriodeCount = stagingList.filter(ligne => ligne.periodeParametrageFacturationList.length === 0).length;
    const periodeSansPrixCount = stagingList
      .reduce((periodeList, ligne) => periodeList.concat(ligne.periodeParametrageFacturationList), [] as PeriodeParametrageFacturationProduitComplementaire[])
      .filter(periode => periode.dateDebut == null || periode.prixVenteHT == null)
      .length;

    const errorList: string[] = [];
    if (ligneSansTaxeCount > 0) {
      errorList.push(`${ligneSansTaxeCount} ligne(s) sans taxe`);
    }
    if (ligneSansPeriodeCount > 0) {
      errorList.push(`${ligneSansPeriodeCount} ligne(s) sans période de prix`);
    }
    if (periodeSansPrixCount > 0) {
      errorList.push(`${periodeSansPrixCount} période(s) sans prix de vente`);
    }
    return errorList;
  });

  readonly isReadyToSaveStaging = computed(() => this.stagingList().length > 0 && this.stagingErrorList().length === 0);

  /** Un bouton grisé sans explication laissait l'utilisateur deviner ce qui manquait : le tooltip le dit. */
  readonly stagingSaveTooltip = computed(() => {
    const errorList = this.stagingErrorList();
    return errorList.length > 0
      ? `Enregistrement impossible : ${errorList.join(', ')}.`
      : 'Enregistrer les lignes de paramétrage';
  });

  closeStagingPopup = (): void => this.isStagingPopupVisible.set(false);

  saveStagingList = async (): Promise<void> => {
    const stagingList = this.stagingList();
    const lignesToAdd = stagingList.filter(ligne => ligne.id == null).map(ligne => this.buildLigneDTO(ligne));
    const lignesToEdit = stagingList.filter(ligne => ligne.id != null).map(ligne => this.buildLigneDTO(ligne));

    const success = await this.persistLignes(lignesToAdd, lignesToEdit);
    if (!success) {
      return;
    }

    this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Paramétrage de facturation au produit complémentaire enregistré avec succès`);
    this.closeStagingPopup();
    this.closeAddArea();
  };

  private buildLigneDTO = (ligne: LigneParametrageFacturationProduitComplementaire): LigneParametrageFacturationProduitComplementaireDTO => ({
    id: ligne.id ?? undefined,
    pointFacturationId: ligne.pointFacturationId,
    plcId: ligne.plcId,
    produitDeclinaisonId: ligne.produitDeclinaisonId,
    taxeId: ligne.taxeId,
    periodeParametrageFacturationList: ligne.periodeParametrageFacturationList.map(periode => ({
      id: periode.id ?? undefined,
      dateDebut: periode.dateDebut,
      uniteDeMesureId: periode.uniteDeMesureId,
      mcpConditionnementVarianteId: periode.mcpConditionnementVarianteId,
      prixVenteHT: periode.prixVenteHT
    }))
  });

  private toLigneParametrageFacturationProduitComplementaire = (ligne: LigneParametrageFacturationProduitComplementaireDTO): LigneParametrageFacturationProduitComplementaire => ({
    id: ligne.id ?? null,
    pointFacturationId: ligne.pointFacturationId,
    plcId: ligne.plcId,
    plcLibelle: ligne.plcLibelle,
    produitDeclinaisonId: ligne.produitDeclinaisonId,
    produitLibelle: ligne.produitLibelle,
    taxeId: ligne.taxeId,
    taxeLibelle: ligne.taxeLibelle,
    periodeParametrageFacturationList: (ligne.periodeParametrageFacturationList ?? []).map(periode => ({
      id: periode.id ?? null,
      dateDebut: periode.dateDebut,
      uniteDeMesureId: periode.uniteDeMesureId,
      mcpConditionnementVarianteId: periode.mcpConditionnementVarianteId,
      uniteDeMesure: periode.uniteDeMesure,
      prixVenteHT: periode.prixVenteHT,
      periodeToDelete: false,
      conditionnementObsolete: periode.conditionnementObsolete ?? false
    }))
  });

  /** PLC de tous les points de facturation sélectionnés, dédupliqués — la map vient du backend en JSON, pas en Map réelle. */
  private dedupeByPointFacturation = (pointFacturationIdList: number[], plcByPointFacturationIdMap: Map<number, NamedItem[]>): NamedItem[] => {
    const plcByIdMap = new Map<number, NamedItem>();
    pointFacturationIdList.forEach(pointFacturationId =>
      (plcByPointFacturationIdMap[pointFacturationId] ?? []).forEach(plc => plcByIdMap.set(plc.id, plc)));
    return [...plcByIdMap.values()];
  };
}
